import { Link } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { ChipComponent } from "src/shared/components/ChipComponent";
import { TSnackbarVariant } from "src/shared/components/SnackbarComponent";
import {
  colorsByEasyServiceType,
  colorsByPartnersCompany,
  colorsByPartnerCategory,
} from "src/shared/constants/partners/colors";
import { routes } from "src/shared/constants/routes.constants";
import { IAddressShort } from "src/shared/types/address.types";
import { EAgencies } from "src/shared/types/common.types";
import { ILicenseFormValues } from "src/shared/types/companies.types";
import {
  EMEssengers,
  EPartnerEasyService,
  IPartnerDto,
  EOrganizationForm,
} from "src/shared/types/partner.types";
import { IBankAccountFormValues } from "src/shared/types/partnerBankAccount";
import {
  IPartnerContactBaseFormValues,
  IPartnerContactFormValues,
  IPartnerFormValues,
} from "../types";

export const licenseDefaultValue: ILicenseFormValues = {
  number: "",
  issued_date: null,
  issued_by: "",
};
const bankDefaultValues: IBankAccountFormValues = {
  id: "",
  correspondent_account: "",
  current_account: "",
  transit_account: "",
  bank_id: "",
  bank_address: null,
  bank_bic: null,
  bank_name: null,
  correspondent_bank_id: "",
  correspondent_bank_bic: null,
  correspondent_bank_name: null,
};

export const addressDefaultValue: IAddressShort = {
  country_id: "",
  region: "",
  area: "",
  city: "",
  address: "",
  postcode: "",
  type: "legal",
};
export const contactBaseDefaultValue: IPartnerContactBaseFormValues = {
  firstname: "",
  lastname: "",
  sex: "",
  position: "",
  email: "",
  phone_additional: "",
  phone_main: "",
  feedback_call_consent: false,
  is_decision_maker: false,
  is_main_contact: false,
  messengers: [{ messenger: EMEssengers.SKYPE, login: "" }],
};
export const postPartnerFormDefaultValues: IPartnerFormValues = {
  partner: {
    agency: "",
    registration_country_id: "",
    registration_number: "",
    main_manager_id: "",
    organization_form: "",
    ownership_id: "",
    name: "",
    nonresident_phone: "",
    nonresident_email: "",
    status: "",
    priority: "",
    phone: "",
    fax: "",
    region: "",
    activity_spheres: [],
    easy_service: 1,
    easy_service_phone: 1,
    carrot_quest: 1,
    is_EDMS_enabled: false,
    edin_email: [],
    is_resident: true,
    is_nda_signed: false,
    nda_note: "",
    is_survey_available: false,
    is_in_blacklist: false,
    okpo: "",
    ogrn: "",
    kpp: "",
    state_registration_certificate: "",
  },
  isPostAddressTheSame: false,
  isAdditionalAddressOn: true,
  license: {
    number: "",
    issued_by: "",
    issued_date: null,
  },
  contact_persons: [{ ...contactBaseDefaultValue, is_main_contact: true }],
  addresses: [
    addressDefaultValue,
    { ...addressDefaultValue, type: "postal" },
    { ...addressDefaultValue, type: "postal_additional" },
  ],
  bank_account: bankDefaultValues,
};

export const contactDefaultValues: IPartnerContactFormValues = {
  firstname: "",
  partner_id: "",
  technical_knowledge_level: "",
  surname: "",
  birthday: null,
  email_marketing_consent: false,
  loyalty_level: "",
  emotional_level: "",
  is_enabled: false,
  note: "",
  personality_type: "",
  lastname: "",
  sex: "",
  position: "",
  email: "",
  phone_additional: "",
  phone_main: "",
  feedback_call_consent: false,
  is_decision_maker: false,
  is_main_contact: false,
  messengers: [{ messenger: EMEssengers.SKYPE, login: "" }],
  access_to_pcp_enabled: false,
  access_to_pcp_finances_enabled: false,
  disable_pcp_access_reason: "",
};

export const partnerColumnsList: GridColDef<IPartnerDto>[] = [
  {
    headerName: "ID",
    type: "string",
    field: "id",
    width: 30,
  },
  {
    headerName: "Наименование",
    type: "string",
    field: "name",
    flex: 2,
    valueGetter: (_value, row) => `${row.name} (${row.easy_service.name})`,
    renderCell({ row }) {
      const organizationForm = row.organization_form;
      const isPrivateIndividual =
        Number(organizationForm?.value) === EOrganizationForm.PrivateIndividual;

      return (
        <div>
          <Link key={row.id} href={`${routes.partners}/${row.id}`}>
            {row.name}
          </Link>{" "}
          {row.easy_service.value === EPartnerEasyService.Disabled ? null : (
            <ChipComponent
              label={row.easy_service.name}
              backgroundColor={colorsByEasyServiceType[row.easy_service.value]}
            />
          )}
          {isPrivateIndividual && organizationForm?.name && (
            <ChipComponent
              label={organizationForm.name}
              backgroundColor="#c573f4"
            />
          )}
        </div>
      );
    },
  },
  {
    headerName: "Компания",
    type: "string",
    field: "agency",
    flex: 1,
    valueGetter: (_value, row) => row.agency.name,
    renderCell({ row }) {
      return row.agency.value === EAgencies.Digital ||
        row.agency.value === EAgencies.Webcom_KZ ? null : (
        <ChipComponent
          label={row.agency.name}
          backgroundColor={colorsByPartnersCompany[row.agency.value]}
        />
      );
    },
  },
  {
    headerName: "Категория",
    type: "string",
    field: "priority",
    flex: 1,
    valueGetter: (_value, row) => row.priority.name,
    renderCell({ row }) {
      return (
        <ChipComponent
          label={row.priority.name}
          backgroundColor={colorsByPartnerCategory[row.priority.value]}
        />
      );
    },
  },
  {
    headerName: "Сотрудник",
    type: "string",
    field: "creator",
    flex: 1,
    valueGetter: (_value, row) =>
      `${row.creator.lastname} ${row.creator.firstname}`,
  },
];

export const INTEGRATION_1C_STATUSES = [
  "SUCCESS",
  "ERP_ONLY",
  "IN_QUEUE",
  "ERROR",
] as const;

export type TIntegration1CStatus = (typeof INTEGRATION_1C_STATUSES)[number];

export interface IIntegration1CMessage {
  text: string;
  variant: TSnackbarVariant;
}

export const INTEGRATION_1C_MESSAGES: Record<
  TIntegration1CStatus,
  IIntegration1CMessage
> = {
  SUCCESS: {
    text: "Контрагент успешно создан в ERP и в 1С",
    variant: "success",
  },
  ERP_ONLY: {
    text: "Контрагент успешно создан в ERP.",
    variant: "success",
  },
  IN_QUEUE: {
    text: "Контрагент в процессе создания в 1С. Пожалуйста, подождите какое-то время прежде чем создавать договоры. При необходимости обновите страницу, чтобы убедиться, что предупреждение исчезло.",
    variant: "warning",
  },
  ERROR: {
    text: "Контрагент не был создан в 1С. Пожалуйста, обратитесь к бухгалтеру, чтобы добавить контрагента и далее создавать договоры.",
    variant: "error",
  },
};
